import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress,
  Fab,
  Tooltip,
  Checkbox,
  Toolbar,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  TableChart as TableIcon,
  Slideshow as SlideshowIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { openFilePreviewOrDownload } from '../utils/filePreview';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import api from '../services/api';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';

interface Document {
  id: number;
  user_id: number;
  user_name: string;
  document_type: string;
  title: string;
  description?: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  folder_path?: string;
  tags: string[];
  access_level: 'private' | 'restricted' | 'public';
  expires_at?: string;
  uploaded_by: number;
  uploaded_by_name: string;
  uploaded_at: string;
  updated_at: string;
  can_access: boolean;
}

interface Folder {
  id: number;
  user_id: number;
  name: string;
  path: string;
  parent_path?: string;
  access_level: 'private' | 'restricted' | 'public';
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  can_access: boolean;
  is_personal_root?: boolean;
}

interface FileExplorerProps {
  userId?: number;
  readOnly?: boolean;
  isHrAdmin?: boolean;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  userId: userIdProp,
  readOnly = false,
  isHrAdmin = false,
}) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [archiveUserId, setArchiveUserId] = useState<number | null>(userIdProp ?? null);
  const [personalRoot, setPersonalRoot] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [bootstrapped, setBootstrapped] = useState(false);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  // Form states
  const [uploadForm, setUploadForm] = useState({
    user_id: userIdProp || user?.id || 0,
    folder_path: currentFolder,
    access_level: 'private' as 'private' | 'restricted' | 'public',
    description: '',
    document_type: 'other' as string,
  });
  const [folderForm, setFolderForm] = useState({
    user_id: userIdProp || user?.id || 0,
    name: '',
    parent_path: '',
    access_level: 'private' as 'private' | 'restricted' | 'public',
  });
  const [bulkForm, setBulkForm] = useState({
    operation: 'delete' as 'delete' | 'move',
    folder_path: '',
  });

  const ownerUserId = archiveUserId ?? user?.id ?? 0;
  const atHrCompanyRoot = isHrAdmin && !archiveUserId && currentFolder === '';
  const canModifyContent = !readOnly && !atHrCompanyRoot && ownerUserId > 0;

  // File handling
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notifications
  const fieldErrors = useFormFieldErrors();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '50',
        ...(searchQuery && { search: searchQuery }),
        ...(filterType && { type: filterType }),
      });
      if (isHrAdmin) {
        if (archiveUserId) {
          params.set('user_id', String(archiveUserId));
          if (currentFolder) {
            params.set('folder_path', currentFolder);
          }
        }
      } else if (currentFolder) {
        params.set('folder_path', currentFolder);
      }

      const response = await api.get(`/hr/documents/archive?${params}`);
      setDocuments(response.data.documents);
      setFolders(response.data.folders);

      const root = response.data.personal_root as string | undefined;
      if (root !== undefined) {
        setPersonalRoot(root);
      }
      if (!isHrAdmin && !bootstrapped && response.data.current_folder) {
        const folder = response.data.current_folder as string;
        setCurrentFolder(folder);
        setUploadForm((prev) => ({ ...prev, folder_path: folder, user_id: user?.id ?? 0 }));
        setBootstrapped(true);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load documents',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [currentFolder, archiveUserId, searchQuery, filterType, isHrAdmin, bootstrapped, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(acceptedFiles);
    setUploadDialogOpen(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled: !canModifyContent,
  });

  const navigateToCompanyRoot = () => {
    setArchiveUserId(null);
    setCurrentFolder('');
    setSelectedItems(new Set());
    setUploadForm((prev) => ({ ...prev, folder_path: '', user_id: user?.id ?? 0 }));
  };

  const navigateToFolder = (folderPath: string, folderOwnerId?: number) => {
    if (isHrAdmin && folderOwnerId != null) {
      setArchiveUserId(folderOwnerId);
    }
    setCurrentFolder(folderPath);
    setSelectedItems(new Set());
    setUploadForm((prev) => ({
      ...prev,
      folder_path: folderPath,
      user_id: folderOwnerId ?? archiveUserId ?? user?.id ?? 0,
    }));
  };

  const navigateUp = () => {
    if (!currentFolder) return;
    if (isHrAdmin && archiveUserId && personalRoot && currentFolder === personalRoot) {
      navigateToCompanyRoot();
      return;
    }
    if (!isHrAdmin && personalRoot && currentFolder === personalRoot) {
      return;
    }
    const pathParts = currentFolder.split('/');
    pathParts.pop();
    const newPath = pathParts.join('/');
    setCurrentFolder(newPath);
    setSelectedItems(new Set());
    setUploadForm((prev) => ({ ...prev, folder_path: newPath }));
  };

  const getBreadcrumbs = () => {
    if (isHrAdmin && !archiveUserId) {
      return [{ name: 'All employees', path: '__company_root__' }];
    }
    const rootLabel = isHrAdmin
      ? (personalRoot.split('/')[0] || 'Employee')
      : (user?.name || personalRoot.split('/')[0] || 'My folder');
    const rootPath = personalRoot || currentFolder.split('/')[0] || '';
    if (!currentFolder || currentFolder === rootPath) {
      const crumbs = isHrAdmin
        ? [
            { name: 'All employees', path: '__company_root__' },
            { name: rootLabel, path: rootPath },
          ]
        : [{ name: rootLabel, path: rootPath }];
      return crumbs;
    }
    const relative = currentFolder.startsWith(`${rootPath}/`)
      ? currentFolder.slice(rootPath.length + 1)
      : currentFolder;
    const relativeParts = relative.split('/').filter(Boolean);
    const base = isHrAdmin
      ? [
          { name: 'All employees', path: '__company_root__' },
          { name: rootLabel, path: rootPath },
        ]
      : [{ name: rootLabel, path: rootPath }];
    return [
      ...base,
      ...relativeParts.map((part, index) => ({
        name: part,
        path: [rootPath, ...relativeParts.slice(0, index + 1)].join('/'),
      })),
    ];
  };

  const handleBreadcrumbClick = (path: string) => {
    if (path === '__company_root__') {
      navigateToCompanyRoot();
      return;
    }
    navigateToFolder(path, archiveUserId ?? undefined);
  };

  const handleEnsurePersonalFolders = async () => {
    if (!window.confirm('Create personal folders (with default subfolders) for all users who do not have one yet?')) {
      return;
    }
    try {
      const response = await api.post('/hr/documents/archive/personal-folders/ensure', {});
      setSnackbar({
        open: true,
        message: response.data.message || 'Personal folders created',
        severity: 'success',
      });
      loadData();
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to create personal folders',
        severity: 'error',
      });
    }
  };

  // File operations
  const handleDownload = async (doc: Document) => {
    try {
      const response = await api.get(`/hr/documents/archive/${doc.id}/download`, {
        responseType: 'blob',
      });

      openFilePreviewOrDownload(new Blob([response.data]), doc.original_filename);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to download file',
        severity: 'error',
      });
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    if (folder.is_personal_root) return;
    if (!window.confirm('Are you sure you want to delete this folder?')) return;

    try {
      await api.delete(`/hr/documents/archive/folders/${folder.id}`);
      setSnackbar({
        open: true,
        message: 'Folder deleted successfully',
        severity: 'success',
      });
      loadData();
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to delete folder',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/hr/documents/archive/${documentId}`);
      setSnackbar({
        open: true,
        message: 'Document deleted successfully',
        severity: 'success',
      });
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete document',
        severity: 'error',
      });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    formData.append('user_id', String(ownerUserId));
    if (uploadForm.folder_path) formData.append('folder_path', uploadForm.folder_path);
    formData.append('access_level', 'private');
    if (uploadForm.description) formData.append('description', uploadForm.description);
    if (uploadForm.document_type) formData.append('document_type', uploadForm.document_type);

    selectedFiles.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });

    try {
      await api.post('/hr/documents/archive/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSnackbar({
        open: true,
        message: `${selectedFiles.length} file(s) uploaded successfully`,
        severity: 'success',
      });

      setUploadDialogOpen(false);
      setSelectedFiles([]);
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to upload files',
        severity: 'error',
      });
    }
  };

  const handleCreateFolder = async () => {
    if (!folderForm.name.trim()) {
      fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
      return;
    }

    try {
      await api.post('/hr/documents/archive/folders', {
        user_id: ownerUserId,
        name: folderForm.name,
        parent_path: currentFolder,
      });

      setSnackbar({
        open: true,
        message: 'Folder created successfully',
        severity: 'success',
      });

      setCreateFolderDialogOpen(false);
      setFolderForm({ ...folderForm, name: '' });
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to create folder',
        severity: 'error',
      });
    }
  };

  const handleBulkOperation = async () => {
    if (selectedItems.size === 0) return;

    try {
      const response = await api.post('/hr/documents/archive/bulk', {
        operation: bulkForm.operation,
        document_ids: Array.from(selectedItems),
        ...(bulkForm.operation === 'move' && { folder_path: bulkForm.folder_path }),
      });

      // Use the message from the backend response
      const message = response.data.message || `Bulk ${bulkForm.operation} completed successfully`;
      const hasFailures = response.data.results?.failed?.length > 0;
      const severity = hasFailures && !response.data.results?.successful?.length ? 'error' : 'success';

      setSnackbar({
        open: true,
        message: message,
        severity: severity,
      });

      setBulkDialogOpen(false);
      setSelectedItems(new Set());
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `Failed to perform bulk ${bulkForm.operation}`;
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  // Selection handling
  const toggleSelection = (id: number) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedItems(newSelection);
  };

  const selectAll = () => {
    const allIds = documents.map(doc => doc.id);
    setSelectedItems(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  // File type icons
  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <ImageIcon color="success" />;
      case 'xls':
      case 'xlsx':
        return <TableIcon color="success" />;
      case 'ppt':
      case 'pptx':
        return <SlideshowIcon color="warning" />;
      default:
        return <DescriptionIcon color="action" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="h2">
            {isHrAdmin && atHrCompanyRoot ? 'Employee personal folders' : 'My Documents'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {isHrAdmin && atHrCompanyRoot && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEnsurePersonalFolders}
              >
                Create missing personal folders
              </Button>
            )}
            {canModifyContent && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={() => setCreateFolderDialogOpen(true)}
                  size="small"
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  size="small"
                >
                  Upload
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {getBreadcrumbs().map((crumb, index) => (
            <Link
              key={crumb.path}
              component="button"
              variant="body2"
              onClick={() => handleBreadcrumbClick(crumb.path)}
              sx={{
                color: index === getBreadcrumbs().length - 1 ? 'text.primary' : 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Type"
              MenuProps={getZoomCompensatedSelectMenuProps()}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="certificate">Certificate</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="identification">Identification</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={viewMode === 'grid'}
                onChange={(e) => setViewMode(e.target.checked ? 'grid' : 'list')}
                size="small"
              />
            }
            label="Grid View"
          />
        </Box>
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedItems.size > 0 && canModifyContent && (
        <Toolbar sx={{ bgcolor: 'action.selected' }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {selectedItems.size} item(s) selected
          </Typography>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={selectAll}
          >
            Select All
          </Button>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearSelection}
          >
            Clear
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            size="small"
            color="primary"
            onClick={() => setBulkDialogOpen(true)}
          >
            Bulk Actions
          </Button>
        </Toolbar>
      )}

      {/* Loading */}
      {loading && <LinearProgress />}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }} {...getRootProps()}>
        <input {...getInputProps()} />

        {isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="primary">
              Drop files here to upload
            </Typography>
          </Box>
        )}

        {viewMode === 'list' ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedItems.size > 0 && selectedItems.size < documents.length}
                      checked={documents.length > 0 && selectedItems.size === documents.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAll();
                        } else {
                          clearSelection();
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Folders */}
                {folders.map((folder) => (
                  <TableRow key={`folder-${folder.id}`} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.has(folder.id)}
                        onChange={() => toggleSelection(folder.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon color="primary" />
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigateToFolder(folder.path, folder.user_id)}
                        >
                          {folder.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>Folder</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      {new Date(folder.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {canModifyContent && folder.can_access && !folder.is_personal_root && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFolder(folder)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Documents */}
                {documents.map((doc) => (
                  <TableRow key={`doc-${doc.id}`} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.has(doc.id)}
                        onChange={() => toggleSelection(doc.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(doc.original_filename)}
                        <Box>
                          <Typography variant="body2">{doc.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doc.document_type}
                        size="small"
                        variant="outlined"
                        color={
                          doc.document_type === 'contract' ? 'primary' :
                            doc.document_type === 'certificate' ? 'success' :
                              doc.document_type === 'identification' ? 'info' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {doc.uploaded_by_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {doc.can_access && (
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(doc)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canModifyContent && doc.can_access && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(doc.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // Grid View
          <Grid container spacing={2}>
            {folders.map((folder) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`folder-${folder.id}`}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigateToFolder(folder.path, folder.user_id)}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="subtitle2" noWrap>
                      {folder.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {documents.map((doc) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`doc-${doc.id}`}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ mb: 1 }}>
                      {getFileIcon(doc.original_filename)}
                    </Box>
                    <Typography variant="subtitle2" noWrap title={doc.title}>
                      {doc.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {doc.original_filename}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={doc.document_type}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(doc.file_size)}
                    </Typography>
                  </CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                    {doc.can_access && (
                      <IconButton size="small" onClick={() => handleDownload(doc)}>
                        <DownloadIcon />
                      </IconButton>
                    )}
                    {canModifyContent && doc.can_access && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(doc.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {documents.length === 0 && folders.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FolderOpenIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              This folder is empty
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload files or create folders to get started
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          setSelectedFiles(files);
          setUploadDialogOpen(true);
        }}
      />

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Selected files: {selectedFiles.map(f => f.name).join(', ')}
            </Typography>

            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadForm.document_type}
                onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                label="Document Type"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                <MenuItem value="diploma">Diploma/Certificate</MenuItem>
                <MenuItem value="certificate">Professional Certificate</MenuItem>
                <MenuItem value="contract">Employment Contract</MenuItem>
                <MenuItem value="identification">Identification Document</MenuItem>
                <MenuItem value="other">Other Document</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of the document..."
            />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={selectedFiles.length === 0}>
            Upload {selectedFiles.length} file(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderDialogOpen}
        onClose={() => setCreateFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Folder Name"
              value={folderForm.name}
              error={fieldErrors.invalid('name')}
              onChange={(e) => {
                fieldErrors.clearField('name');
                setFolderForm({ ...folderForm, name: e.target.value });
              }}
              fullWidth
              required
            />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">
            Create Folder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Operations Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Operations</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Operation</InputLabel>
              <Select
                value={bulkForm.operation}
                onChange={(e) => setBulkForm({ ...bulkForm, operation: e.target.value as any })}
                label="Operation"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                <MenuItem value="delete">Delete Selected</MenuItem>
                <MenuItem value="move">Move to Folder</MenuItem>
              </Select>
            </FormControl>

            {bulkForm.operation === 'move' && (
              <TextField
                label="Target Folder Path"
                value={bulkForm.folder_path}
                onChange={(e) => setBulkForm({ ...bulkForm, folder_path: e.target.value })}
                fullWidth
                placeholder="e.g., Paychecks"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkOperation} variant="contained" color="warning">
            Execute {bulkForm.operation}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileExplorer;